import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Search, ArrowRight, Home } from "lucide-react";

const POPULAR: { label: string; to: string; blurb: string }[] = [
  { label: "SEO Services", to: "/services", blurb: "Technical, on-page, off-page, local." },
  { label: "AEO Service", to: "/services/aeo", blurb: "Get cited by ChatGPT & AI Overviews." },
  { label: "Case Studies", to: "/case-studies", blurb: "Real client results, real numbers." },
  { label: "Blog", to: "/blog", blurb: "Deep guides on SEO, AEO & GEO." },
  { label: "Free SEO Audit", to: "/free-seo-audit", blurb: "I'll review your site for free." },
  { label: "AEO Checklist", to: "/aeo-checklist", blurb: "The 47-item implementation PDF." },
  { label: "About", to: "/about", blurb: "Who runs this — and why." },
  { label: "Contact", to: "/contact", blurb: "Book a 20-min scoping call." },
];

/**
 * Real 404 page. Replaces the previous silent redirect to `/`.
 * Gives users a search (jumps to the blog search), a list of popular
 * destinations, and a "Go home" affordance. SEO-safe — emits its own
 * <title> and a noindex meta via the route boundary above.
 */
export function NotFoundPage() {
  const [q, setQ] = useState("");

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const term = q.trim();
    window.location.href = term ? `/blog?q=${encodeURIComponent(term)}` : "/blog";
  };

  return (
    <section className="container-x py-24 lg:py-32">
      <title>404 — Page not found | Freelancer Tamal</title>
      <meta name="robots" content="noindex, follow" />
      <div className="max-w-2xl">
        <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs uppercase tracking-[0.2em] text-primary">
          404
        </div>
        <h1 className="mt-6 font-display text-5xl md:text-7xl leading-[1.02]">
          That page wandered off.
        </h1>
        <p className="mt-6 text-lg text-muted-foreground">
          The URL you tried doesn't exist — maybe it moved, or the link was typed wrong.
          Search the blog, or pick one of the popular pages below.
        </p>

        <form
          onSubmit={onSubmit}
          className="mt-10 flex items-center gap-2 rounded-full border border-border bg-card p-2"
        >
          <Search className="ml-3 size-4 text-muted-foreground" aria-hidden />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search the blog…"
            aria-label="Search the blog"
            className="min-w-0 flex-1 bg-transparent px-2 py-2 text-sm focus:outline-none"
          />
          <button
            type="submit"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 sm:px-5 py-2.5 text-sm font-medium hover:shadow-[var(--shadow-glow)]"
          >
            Search <ArrowRight size={14} />
          </button>
        </form>

        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <Home size={14} /> Or go back to the homepage
        </Link>
      </div>

      <div className="mt-20">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Popular pages
        </div>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {POPULAR.map((p) => (
            <Link
              key={p.to}
              to={p.to}
              className="group block rounded-2xl border border-border bg-card p-6 hover:border-primary/40 hover:shadow-[var(--shadow-glow)] transition"
            >
              <div className="flex items-start justify-between gap-2">
                <h2 className="font-display text-lg leading-tight">{p.label}</h2>
                <ArrowRight
                  size={14}
                  className="mt-1 text-muted-foreground group-hover:text-primary transition"
                />
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{p.blurb}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}